// src/components/site/SiteLayout.js
// Header + footer for the public marketing and legal pages (home, privacy, terms).
// Google OAuth verification needs these reachable without signing in.
import React, { useEffect, useState } from 'react';
import LogoMark, { APP_NAME } from '../LogoMark';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import '../../styles/site.css';

export const SITE = {
  name: APP_NAME,
  url: 'https://postcalender.com',
  company: 'Ausy Maintanance Services Pvt Limited',
  email: 'ausydigital@gmail.com',
  jurisdiction: 'Queensland, Australia',
};

export function Logo({ size = 30 }) {
  return (
    <Link to="/" className="site-logo" aria-label={`${SITE.name} home`}>
      <LogoMark size={size} />
      <span>{SITE.name}</span>
    </Link>
  );
}

// SPA navigation keeps the old scroll position; reset it, or jump to #anchor targets.
function useScrollToTarget() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' }), 0);
    return () => clearTimeout(t);
  }, [pathname, hash]);
}

function Header() {
  const user = useStore(s => s.user);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);

  return (
    <header className="site-header">
      <div className="site-container site-header-inner">
        <Logo />
        <button className="site-menu-btn" aria-label="Toggle menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
          <span /><span /><span />
        </button>
        <nav className={`site-nav${open ? ' open' : ''}`}>
          <Link to="/#features">Features</Link>
          <Link to="/#platforms">Platforms</Link>
          <Link to="/#how-it-works">How it works</Link>
          <Link to="/privacy">Privacy</Link>
          <div className="site-nav-cta">
            {user ? (
              <Link to="/dashboard" className="site-btn site-btn-primary">Open dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="site-btn site-btn-ghost">Sign in</Link>
                <Link to="/register" className="site-btn site-btn-primary">Get started</Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="site-container site-footer-grid">
        <div className="site-footer-brand">
          <Logo size={26} />
          <p>Plan, schedule and publish social media posts to every channel your business uses, from one calendar.</p>
          <a href={`mailto:${SITE.email}`} className="site-footer-email">{SITE.email}</a>
        </div>
        <div>
          <h4>Product</h4>
          <Link to="/#features">Features</Link>
          <Link to="/#platforms">Supported platforms</Link>
          <Link to="/#how-it-works">How it works</Link>
        </div>
        <div>
          <h4>Account</h4>
          <Link to="/login">Sign in</Link>
          <Link to="/register">Create an account</Link>
          <Link to="/forgot-password">Reset password</Link>
        </div>
        <div>
          <h4>Legal</h4>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy#data-deletion">Data deletion</Link>
        </div>
      </div>
      <div className="site-container site-footer-bottom">
        <span>© {year} {SITE.name}. Operated by {SITE.company}.</span>
        <span>
          <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </span>
      </div>
    </footer>
  );
}

export default function SiteLayout({ children }) {
  useScrollToTarget();
  return (
    <div className="site">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

// Shared shell for the Privacy Policy and Terms pages: title band + table of contents + body.
export function LegalPage({ title, updated, intro, sections }) {
  useEffect(() => { document.title = `${title} | ${SITE.name}`; return () => { document.title = SITE.name; }; }, [title]);
  return (
    <SiteLayout>
      <section className="legal-hero">
        <div className="site-container">
          <div className="site-eyebrow">Legal</div>
          <h1>{title}</h1>
          <p>Last updated: {updated}</p>
        </div>
      </section>
      <div className="site-container legal-layout">
        <aside className="legal-toc">
          <div className="legal-toc-title">On this page</div>
          {sections.map((s, i) => <a key={s.id} href={`#${s.id}`}>{i + 1}. {s.title}</a>)}
        </aside>
        <article className="legal-body">
          {intro}
          {sections.map((s, i) => (
            <section key={s.id} id={s.id}>
              <h2>{i + 1}. {s.title}</h2>
              {s.body}
            </section>
          ))}
        </article>
      </div>
    </SiteLayout>
  );
}
